// This component is installed from untitledUI, and we know that the methods don't use this
// oxlint-disable typescript/unbound-method
import type { SliderProps as AriaSliderProps } from 'react-aria-components';

import { useLayoutEffect, useRef } from 'react';
import {
  Label as AriaLabel,
  Slider as AriaSlider,
  SliderOutput as AriaSliderOutput,
  SliderThumb as AriaSliderThumb,
  SliderTrack as AriaSliderTrack,
} from 'react-aria-components';

import { cx, sortCx } from '@/utils/cx';

const styles = sortCx({
  default: 'hidden',
  bottom:
    'absolute top-2 left-1/2 -translate-x-1/2 translate-y-full text-md font-medium text-primary',
  'top-floating':
    'absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-secondary shadow-lg ring-1 ring-secondary_alt',
});

interface SliderProps<T extends number | number[]> extends AriaSliderProps<T> {
  labelPosition?: keyof typeof styles;
  labelFormatter?: (value: number, index: number) => string;
  thumbAriaLabels?: string[];
  showLabelOnInteraction?: boolean;
}

function Thumb({
  index,
  label,
  ariaLabel,
  labelPosition,
  formattedValue,
  showLabelOnInteraction,
}: {
  index: number;
  label?: string;
  ariaLabel?: string;
  labelPosition: keyof typeof styles;
  formattedValue: string;
  showLabelOnInteraction: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (label) inputRef.current?.setAttribute('aria-valuetext', label);
  });

  return (
    <AriaSliderThumb
      index={index}
      inputRef={inputRef}
      aria-label={ariaLabel}
      className={({ isFocusVisible, isDragging }) =>
        cx(
          'top-1/2 box-border size-6 cursor-grab rounded-full bg-slider-handle-bg shadow-md ring-2 ring-slider-handle-border ring-inset',
          showLabelOnInteraction && 'group',
          isFocusVisible && 'outline-2 outline-offset-2 outline-focus-ring',
          isDragging && 'cursor-grabbing',
        )
      }
    >
      <AriaSliderOutput
        className={cx(
          'whitespace-nowrap',
          styles[labelPosition],
          showLabelOnInteraction &&
            'hidden group-data-focus-visible:block group-dragging:block',
        )}
      >
        {label ?? formattedValue}
      </AriaSliderOutput>
    </AriaSliderThumb>
  );
}

export const Slider = <T extends number | number[] = number | number[]>({
  labelPosition = 'default',
  minValue = 0,
  maxValue = 100,
  labelFormatter,
  thumbAriaLabels,
  showLabelOnInteraction = false,
  formatOptions,
  ...rest
}: SliderProps<T>) => {
  // Format thumb value as percentage by default.
  const defaultFormatOptions: Intl.NumberFormatOptions = {
    style: 'percent',
    maximumFractionDigits: 0,
  };

  return (
    <AriaSlider<T>
      {...rest}
      {...{ minValue, maxValue }}
      formatOptions={formatOptions ?? defaultFormatOptions}
    >
      <AriaLabel />
      <AriaSliderTrack className="relative h-6 w-full">
        {({
          state: { values, getThumbValue, getThumbPercent, getFormattedValue },
        }) => {
          const left = values.length === 1 ? 0 : getThumbPercent(0);
          const width =
            values.length === 1
              ? getThumbPercent(0)
              : getThumbPercent(1) - left;

          return (
            <>
              <span className="absolute top-1/2 h-2 w-full -translate-y-1/2 rounded-full bg-quaternary" />
              <span
                className={cx(
                  'absolute top-1/2 h-2 w-full -translate-y-1/2 rounded-full',
                  rest.isDisabled ? 'bg-utility-gray-500' : 'bg-brand-solid',
                )}
                style={{
                  left: `${left * 100}%`,
                  width: `${width * 100}%`,
                }}
              />
              {values.map((_value, index) => (
                <Thumb
                  // oxlint-disable-next-line react/no-array-index-key -- thumb count is stable; values change during drag
                  key={`thumb-${index}`}
                  index={index}
                  label={labelFormatter?.(getThumbValue(index), index)}
                  ariaLabel={thumbAriaLabels?.[index]}
                  labelPosition={labelPosition}
                  formattedValue={getFormattedValue(getThumbValue(index) / 100)}
                  showLabelOnInteraction={showLabelOnInteraction}
                />
              ))}
            </>
          );
        }}
      </AriaSliderTrack>
    </AriaSlider>
  );
};
